// Port of ios/Sources/ViewModels/HTMLEntities.swift.
//
// The HTML entities integration payloads leak into plain text (`&nbsp;`, `&amp;`, numeric
// references) — the webapp's HTML pipeline dissolves them for free, so a native renderer that
// shows the literals reads as broken (user screenshot, 2026-09-04). Decoded BEFORE markdown
// parsing, once, at each renderer's front door.
//
// WEB: the web client renders the decoded string as TEXT (never as HTML), so decoding here
// cannot inject markup — `&lt;b&gt;` becomes the visible characters `<b>`, as on Apple.

const named: ReadonlyArray<readonly [string, string]> = [
  // `&nbsp;` becomes a PLAIN space on purpose: integrations use it as filler, and a real
  // no-break space would glue long runs into unwrappable lines.
  ['&nbsp;', ' '],
  ['&lt;', '<'],
  ['&gt;', '>'],
  ['&quot;', '"'],
  ['&#39;', "'"],
  ['&apos;', "'"],
];

function decode(text: string): string {
  if (!text.includes('&')) return text;
  let out = text;
  for (const [entity, value] of named) {
    out = out.replaceAll(entity, value);
  }
  // Numeric references, decimal and hex.
  if (out.includes('&#')) {
    out = decodeNumeric(out);
  }
  // `&amp;` LAST, or "&amp;nbsp;" would double-decode into a vanished space.
  out = out.replaceAll('&amp;', '&');
  return out;
}

/**
 * Swift: `UInt32(digits, radix:)` then `Unicode.Scalar(value)` — a reference that does not
 * parse in its radix ("&#1a;"), overflows, or names a surrogate / out-of-range value stays as
 * typed.
 */
function decodeNumeric(text: string): string {
  return text.replace(/&#(x?)([0-9a-fA-F]+);/g, (whole, hexFlag: string, digits: string) => {
    const isHex = hexFlag !== '';
    if (!isHex && !/^[0-9]+$/.test(digits)) return whole;
    const value = parseInt(digits, isHex ? 16 : 10);
    if (!Number.isFinite(value) || value > 0x10ffff || (value >= 0xd800 && value <= 0xdfff)) return whole;
    return String.fromCodePoint(value);
  });
}

export const HTMLEntities = {
  decode,
} as const;
